<template>
    <div
        v-if="item.components.length"
        class="list-view-tile"
        data-testid="list-element"
    >
        <div
            class="grid"
            :class="{ 'no-trigger' : !isToggleVisible(item), 'with-remove': allowRemove }"
        >
            <div class="list-view-tile-label-wrapper">
                <CustomPageBlock
                    :block="item.components[0]"
                    :model-value="model[0]"
                    :parent-index="itemIndex"
                    :parent-id="parentId"
                    :data-testid="`custom-page-block-${kebabCase((item.components[0] as ExtendedBlock<CPInputConfiguration>).fieldName || item.components[0].type)}`"
                    @update:model-value="(v: string) => handleModelUpdate(v, 0)"
                />
            </div>

            <div
                v-if="allowRemove"
                class="minor-btn"
                @click="emit('remove-item', itemIndex)"
            >
                <i class="mi-close" />
            </div>

            <div
                v-if="isToggleVisible(item)"
                :class="{ 'isToggled': toggled }"
                class="minor-btn"
                data-testid="list-item-toggle"
                @click="toggle"
            >
                <i class="mi-chevron-down" />
            </div>
        </div>

        <transition
            v-if="item.components?.length > 1"
            name="list-toggle"
            @enter="onEnter"
            @after-enter="onLeave"
            @before-leave="onEnter"
            @after-leave="onLeave"
        >
            <div
                v-show="toggled"
                class="list-view-tile-content"
                data-testid="list-view-components"
            >
                <div
                    v-for="(block, index) in item.components"
                    :key="`${block.id}_${index}`"
                    class="list-view-item"
                >
                    <template v-if="index > 0">
                        <CustomPageBlock
                            :block="block"
                            :model-value="model[index]"
                            :data-testid="`custom-page-block-${kebabCase((block as ExtendedBlock<CPInputConfiguration>).fieldName || block.type)}`"
                            :parent-index="itemIndex"
                            :parent-id="parentId"
                            @update:model-value="(v: string) => handleModelUpdate(v, index)"
                        />
                    </template>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { isNotNil } from 'ramda'
import { clone } from 'widget-components/utilities/clone'
import equals from 'fast-deep-equal/es6'
import type { ListItem } from 'widget-components/types'
import { CustomPageBlock, TooltipBlockDecorator, useBlockDecorators } from 'widget-components/integrations/CustomPage'
import { kebabCase } from 'change-case'
import type { CPInputConfiguration, ExtendedBlock } from 'widget-components/integrations/CustomPage/types'

useBlockDecorators(TooltipBlockDecorator)

const props = defineProps<{
    item: ListItem
    modelValue: Array<string> | null
    itemIndex: number
    parentId: string
    expanded: boolean
    allowRemove?: boolean
}>()

const emit = defineEmits(['update:modelValue', 'download-document', 'remove-item'])

const toggled = ref(false)

const model = ref<Array<string>>([])

onMounted(() => {
    model.value = props.item.components.map(c => c.initialValue)

    if (props.expanded) {
        toggled.value = true
    }
})

const handleModelUpdate = (v: unknown, index: number) => {
    emit('update:modelValue', v, index)
}

const isToggleVisible = computed(() => (item: ListItem) => item.components?.length > 1)

const toggle = () => {
    toggled.value = !toggled.value
}

const onEnter = (el: Element) => {
    (el as HTMLElement).style.height = el.scrollHeight + 'px'
}

const onLeave = (el: Element) => {
    (el as HTMLElement).style.height = ''
}

watch(() => props.modelValue, (val) => {
    if (!equals(val, model.value)) {
        model.value = clone(val as Array<string>)
    }
}, { deep: true })

watch(() => props.item.components.map((b, idx) => idx > 0 && !!b.error), (val, oldVal) => {
    if (val.filter(isNotNil).length && !equals(val, oldVal)) {
        toggled.value = true
    }
}, { deep: true })
</script>

<style lang="sass" scoped>
@import "./ListView.sass"

.grid.with-remove
    grid-template-columns: 1fr 36px 36px
    grid-gap: 8px
</style>
